/**
 * Тема оформления: и подложка карты, и хром приложения.
 *
 * Раньше это были две несвязанные вещи. Переключатель в панели менял только
 * палитру подложки, а интерфейс жёстко следовал системной настройке, и выбрать
 * светлое оформление при тёмной системе было нельзя вовсе. Со стороны это
 * выглядит как сломанный переключатель: человек выбирает «Светлая», а меню
 * остаётся тёмным.
 *
 * Поэтому переключатель один и управляет всем сразу. Два независимых дали бы
 * восемь состояний, из которых осмысленны три-четыре, а «тёмное меню при
 * светлой карте» никто не выбирает осознанно — это и есть та самая поломка
 * глазами пользователя.
 *
 * «Без подложки» — не тема, а режим карты: подложка гасится совсем. Оформление
 * при нём идёт за системой, потому что своего цвета у этого режима нет.
 */

import type { BasemapTheme } from "~/map/basemapStyle";

/** Что выбирает человек. `auto` — «как в системе», значение по умолчанию. */
export type AppTheme = "auto" | "light" | "dark" | "none";

export const THEME_LABELS: Record<AppTheme, string> = {
  auto: "Как в системе",
  light: "Светлая",
  dark: "Тёмная",
  none: "Без подложки",
};

const STORAGE_KEY = "genplan.theme";

/** Цвет строки состояния браузера. Совпадает с фоном хрома приложения. */
const BROWSER_CHROME = { light: "#f4f2ee", dark: "#1b1d21" } as const;

const DARK_QUERY = "(prefers-color-scheme: dark)";

export function normalizeTheme(value: string | null | undefined): AppTheme {
  return value === "light" || value === "dark" || value === "none" || value === "auto"
    ? value
    : "auto";
}

function systemPrefersDark(): boolean {
  return typeof window !== "undefined" && window.matchMedia(DARK_QUERY).matches;
}

/**
 * Во что разворачивается выбор для оформления интерфейса.
 *
 * У режима «без подложки» собственного цвета нет, поэтому он, как и «авто»,
 * отдаётся системе.
 */
export function resolveUiTheme(theme: AppTheme): "light" | "dark" {
  if (theme === "light" || theme === "dark") return theme;
  return systemPrefersDark() ? "dark" : "light";
}

/** Во что разворачивается выбор для подложки карты. */
export function resolveBasemapTheme(theme: AppTheme): BasemapTheme {
  if (theme === "none") return "none";
  if (theme === "light" || theme === "dark") return theme;
  return systemPrefersDark() ? "dark" : "light";
}

/**
 * Применяет тему к документу.
 *
 * Атрибут ставится только при явном выборе. Его отсутствие — это и есть режим
 * «как в системе»: тогда работает медиазапрос в таблице стилей, и никакого
 * состояния хранить не требуется.
 */
export function applyUiTheme(theme: AppTheme): void {
  const root = document.documentElement;
  if (theme === "light" || theme === "dark") root.dataset.theme = theme;
  else delete root.dataset.theme;

  // Цвет строки состояния браузера. В разметке лежат два тега с медиа-условиями
  // под системную настройку; трогать их не нужно. Браузер берёт ПЕРВЫЙ
  // подходящий тег, поэтому ручной выбор достаточно вставить перед ними, а при
  // возврате к системе — убрать.
  const override = document.head.querySelector<HTMLMetaElement>(
    'meta[name="theme-color"][data-manual]',
  );

  if (theme === "auto" || theme === "none") {
    override?.remove();
    return;
  }

  const color = BROWSER_CHROME[resolveUiTheme(theme)];
  if (override) {
    override.content = color;
    return;
  }

  const meta = document.createElement("meta");
  meta.name = "theme-color";
  meta.content = color;
  meta.dataset.manual = "1";
  document.head.insertBefore(meta, document.head.querySelector('meta[name="theme-color"]'));
}

/** Сохранённый выбор. Ссылка важнее хранилища, поэтому читается вторым. */
export function storedTheme(): AppTheme | null {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value ? normalizeTheme(value) : null;
  } catch {
    // Приватный режим и запрет хранилища — не повод ронять карту.
    return null;
  }
}

export function storeTheme(theme: AppTheme): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    /* см. storedTheme */
  }
}

/**
 * Следит за системной настройкой.
 *
 * Нужно для «авто» и «без подложки»: пока выбор отдан системе, смена темы в ОС
 * обязана менять и карту. Возвращает функцию отписки.
 */
export function watchSystemTheme(onChange: () => void): () => void {
  const query = window.matchMedia(DARK_QUERY);
  query.addEventListener("change", onChange);
  return () => {
    query.removeEventListener("change", onChange);
  };
}
